import { useState } from "react";
import {
  ApprovalRequiredChip,
  GAOSAFE_APPROVAL_REASON,
  useWritesBlocked,
} from "../authority";
import {
  apiCall,
  ApiHttpError,
  RouteNotFoundError,
  type WithdrawSuccessResponse,
} from "../api/client";

const EVM_ADDR_RE = /^0x[0-9a-fA-F]{40}$/;
const UINT256_RE = /^(?:0|[1-9][0-9]*)$/;

export interface WithdrawFormFields {
  showAffiliate: boolean;
  path: "/v2/admin/bff/escrow/withdraw-affiliate" | "/v2/admin/bff/escrow/withdraw-treasury";
  buttonLabel: string;
  description: string;
}

export function WithdrawForm(props: WithdrawFormFields) {
  const [token, setToken] = useState("");
  const [affiliate, setAffiliate] = useState("");
  const [amount, setAmount] = useState("");
  const [proposalId, setProposalId] = useState("");
  const [data, setData] = useState<WithdrawSuccessResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingPolicy, setPendingPolicy] = useState(false);
  const [busy, setBusy] = useState(false);

  const tokenValid = EVM_ADDR_RE.test(token);
  const amountValid = UINT256_RE.test(amount) && amount !== "0";
  const affiliateValid = !props.showAffiliate || (EVM_ADDR_RE.test(affiliate) && affiliate !== "0x0000000000000000000000000000000000000000");
  const proposalIdValid = proposalId !== "" && proposalId.length <= 128;

  const canSubmit = tokenValid && amountValid && affiliateValid && proposalIdValid && !busy;

  const writesBlocked = useWritesBlocked();

  async function submit() {
    setError(null);
    setData(null);
    setPendingPolicy(false);
    setBusy(true);
    try {
      const body: Record<string, unknown> = {
        token: token.toLowerCase(),
        amount,
        proposalId,
      };
      if (props.showAffiliate) {
        body.affiliate = affiliate.toLowerCase();
      }
      const resp = await apiCall<WithdrawSuccessResponse>(props.path, {
        method: "POST",
        body,
      });
      setData(resp);
    } catch (e) {
      if (e instanceof ApiHttpError) {
        // Map the BE fail-closed shape — when the signer policy is
        // not yet promoted to allow the new V3 selectors, the BE
        // returns 503 service_unavailable. Surface that as a
        // distinct "pending operator promotion" state rather than a
        // generic error.
        if (e.status === 503) {
          setPendingPolicy(true);
        }
        setError(formatApiError(e));
      } else {
        setError(formatError(e));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <p style={{ color: "var(--fg-dim)", marginTop: 0 }}>{props.description}</p>
      <fieldset
        style={{
          border: "1px solid var(--border)",
          borderRadius: 4,
          padding: 12,
          display: "grid",
          gridTemplateColumns: "120px 1fr",
          gap: 8,
        }}
      >
        <legend style={{ padding: "0 6px", color: "var(--fg-dim)" }}>Request body</legend>
        <label style={{ alignSelf: "center" }}>token</label>
        <input
          value={token}
          onChange={(e) => setToken(e.target.value.trim())}
          placeholder="0x... (Base Sepolia USDC)"
        />
        {props.showAffiliate && (
          <>
            <label style={{ alignSelf: "center" }}>affiliate</label>
            <input
              value={affiliate}
              onChange={(e) => setAffiliate(e.target.value.trim())}
              placeholder="0x... recipient with affiliateWithdrawable bucket"
            />
          </>
        )}
        <label style={{ alignSelf: "center" }}>amount</label>
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value.trim())}
          placeholder="positive uint256 decimal (USDC base units)"
        />
        <label style={{ alignSelf: "center" }}>proposalId</label>
        <input
          value={proposalId}
          onChange={(e) => setProposalId(e.target.value.trim())}
          placeholder="ulid from an approved multisign proposal"
        />
        <div />
        <button
          onClick={writesBlocked ? undefined : submit}
          disabled={writesBlocked || !canSubmit}
          title={writesBlocked ? GAOSAFE_APPROVAL_REASON : undefined}
        >
          {busy ? "Submitting…" : props.buttonLabel}
        </button>
        {writesBlocked && <ApprovalRequiredChip />}
      </fieldset>
      <div
        style={{
          marginTop: 12,
          padding: 10,
          border: "1px solid var(--warn)",
          color: "var(--warn)",
          borderRadius: 4,
          background: "rgba(251,191,36,0.05)",
          fontSize: 12,
        }}
      >
        <strong>Dev/test only.</strong> Even with a valid owner session +
        trusted device + approved multisign proposal + healthy
        kill-switch, the on-chain broadcast still depends on the
        operator promoting the dev/test signer policy on D1 to
        allow the new V3 selectors. Until that operator step
        lands, the BE fail-closes with{" "}
        <code>503 service_unavailable</code> — that is the
        expected dev/test posture, NOT a regression.
      </div>
      {error && (
        <div
          role="alert"
          style={{
            marginTop: 12,
            padding: 12,
            border: "1px solid var(--danger)",
            color: "var(--danger)",
            borderRadius: 4,
            background: "rgba(255,100,100,0.08)",
          }}
        >
          {error}
          {pendingPolicy && (
            <div style={{ marginTop: 8, color: "var(--warn)" }}>
              ↑ This 503 may indicate the operator-managed signer
              policy on D1 has not yet been promoted. See README §3
              (BE dependency).
            </div>
          )}
        </div>
      )}
      {data && (
        <fieldset
          style={{
            border: "1px solid var(--ok)",
            borderRadius: 4,
            padding: 12,
            marginTop: 12,
            background: "rgba(74,222,128,0.05)",
          }}
        >
          <legend style={{ padding: "0 6px", color: "var(--ok)" }}>OK — accepted</legend>
          <pre>{JSON.stringify(data, null, 2)}</pre>
        </fieldset>
      )}
    </div>
  );
}

function formatApiError(e: ApiHttpError): string {
  const body = e.body;
  const code =
    body !== null && typeof body === "object" && "error" in body
      ? String((body as { error: unknown }).error)
      : "unknown";
  const message =
    body !== null && typeof body === "object" && "message" in body
      ? String((body as { message: unknown }).message)
      : "";
  const reason =
    body !== null && typeof body === "object" && "reason" in body
      ? String((body as { reason: unknown }).reason)
      : null;
  return `HTTP ${e.status} ${code}${reason ? ` (${reason})` : ""}${message ? `: ${message}` : ""}`;
}

function formatError(e: unknown): string {
  if (e instanceof RouteNotFoundError) return `BE route missing: ${e.message}`;
  return e instanceof Error ? e.message : String(e);
}
